'use client'

import Button from '@/app/components/ui/buttons/button'
import { logger } from '@/lib/logger'
import { getSafeReturnUrl } from '@/lib/return-url'
import PayK12Logo from '@/public/svg/logo-payk12.svg'
import { UserRole, useAuthSessionStore } from '@/stores/auth-session-store'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { AlertCircle, Eye, EyeOff, Lock, Mail, RefreshCwIcon } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import { FormEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import fetchTokenTyped from '../actions/fetch-token-typed'
import { getSessionFromToken } from '../actions/get-session'

interface LoginFormData {
  username: string
  password: string
  rememberMe: boolean
}

interface LoginResponse {
  token: string
  expiresIn?: number
  tokenType?: string
}

interface SessionResponse {
  isAuthenticated: boolean
  userId: string
  username?: string
  email?: string
  role: number
  tenantId?: number
  schoolIds?: number[]
  departmentIds?: number[]
  categoryIds?: number[]
  permissions?: string[]
}

interface Props {
  onSuccess?: () => void
}

const LoginForm = ({ onSuccess }: Props) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { saveSession, setError, setReturnUrl, getRedirectUrl } = useAuthSessionStore()

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    rememberMe: false,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Partial<LoginFormData>>({})

  // Get return URL from params
  useEffect(() => {
    const returnUrl = searchParams.get('return') || searchParams.get('returnTo')
    if (returnUrl) {
      setReturnUrl(returnUrl)
    }
  }, [searchParams, setReturnUrl])

  // Validation
  const validateForm = (): boolean => {
    const errors: Partial<LoginFormData> = {}

    if (!formData.username) {
      errors.username = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
      errors.username = 'Please enter a valid email'
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle login mutation
  const { mutate: login, isPending: isLoading } = useMutation<LoginResponse, Error, LoginFormData>({
    mutationFn: async (data) => {
      const response = await fetchTokenTyped({
        username: data.username,
        password: data.password,
        rememberMe: data.rememberMe,
        returnUrl: useAuthSessionStore.getState().returnUrl || undefined,
      })

      return {
        token: response.access_token,
        expiresIn: response.expires_in,
        tokenType: response.token_type,
      }
    },
    onSuccess: async (tokenResponse) => {
      try {
        // Get session details
        console.info('Fetching session from token', { tokenResponse })
        const session = await getSessionFromToken(tokenResponse.token)
        logger.info('ses', { session: session })

        // Convert session to expected format
        const sessionData: SessionResponse = {
          isAuthenticated: session?.isAuthenticated || false,
          userId: String(session?.userId || ''),
          username: session?.name,
          email: session?.email,
          role: session?.role || UserRole.Purchaser,
          tenantId: session?.tenantId,
          schoolIds: session?.schoolIds,
          departmentIds: session?.departmentIds,
          categoryIds: [],
          permissions: Array.isArray(session?.permissions) ? session.permissions : [],
        }

        logger.info('sessionData', { session: session ?? {}, sessionData })
        if (!sessionData.isAuthenticated) {
          throw new Error('Failed to authenticate')
        }

        // Save to auth store
        const userRole = sessionData.role as UserRole
        saveSession(
          {
            userId: sessionData.userId,
            username: sessionData.username || sessionData.userId,
            email: sessionData.email,
            role: userRole,
            tenantId: sessionData.tenantId,
            schoolIds: sessionData.schoolIds,
            departmentIds: sessionData.departmentIds,
            categoryIds: sessionData.categoryIds,
            impersonatingUserId: session?.impersonatingUserId ?? null,
            permissions: sessionData.permissions,
            isAuthenticated: true,
          },
          tokenResponse.token,
        )

        // Show success message
        toast.success('Login successful! Redirecting...')

        // Calculate redirect URL based on user role directly
        const getRoleBasedRedirect = (role: UserRole): string => {
          logger.info('getRoleBasedRedirect called', { role, roleName: UserRole[role] })

          const redirectMap: Record<UserRole, string> = {
            [UserRole.SuperAdmin]: '/pages/system/contacts.html',
            [UserRole.SchoolCorpAdmin]: '/pages/system/school-corp-view.html',
            [UserRole.SchoolCorpAdminReadOnly]: '/pages/system/school-corp-view.html?readonly=true',
            [UserRole.SchoolAdmin]: '/pages/system/school-view.html',
            [UserRole.SchoolAdminReadOnly]: '/pages/system/school-view.html?readonly=true',
            [UserRole.DepartmentAdmin]: '/pages/system/school-dept-view.html',
            [UserRole.DepartmentAdminReadOnly]: '/pages/system/school-dept-view.html?readonly=true',
            [UserRole.Purchaser]: '/pages/public/account.html',
          }

          const redirect = redirectMap[role] || '/pages/public/account.html'
          logger.info('Redirecting to:', redirect)
          return redirect
        }

        // PK-1316: honor a safe internal returnUrl (e.g. /login?return=/account)
        // before falling back to the hardcoded role-based legacy pages.
        const returnUrl = getSafeReturnUrl(useAuthSessionStore.getState().returnUrl)
        const redirectTo = returnUrl ?? getRoleBasedRedirect(userRole)
        logger.info('Login successful, redirecting', { redirectTo, role: userRole })

        // Use router.push for Next.js routes, window.location for legacy HTML pages
        if (redirectTo.endsWith('.html')) {
          window.location.href = redirectTo
        } else {
          router.push(redirectTo)
        }

        onSuccess?.()
      } catch (error) {
        logger.error('Failed to process session:', error)
        toast.error('Login failed. Please try again.')
        setError('Failed to process session')
      }
    },
    onError: (error) => {
      logger.error('Login error:', error)

      // Show user-friendly error messages
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        toast.error('Invalid email or password')
        setValidationErrors({
          username: 'Invalid credentials',
          password: 'Invalid credentials',
        })
      } else if (error.message.includes('Network')) {
        toast.error('Network error. Please check your connection.')
      } else {
        toast.error('Login failed. Please try again.')
      }

      setError(error.message)
    },
  })

  // Handle form submission
  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (!validateForm()) {
        toast.error('Please fix the errors below')
        return
      }

      login(formData)
    },
    [formData, login],
  )

  // Handle enter key press
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault()
      if (validateForm()) {
        login(formData)
      }
    }
  }

  // Handle input changes
  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit}>
      {/* Logo */}
      <div className="mb-4 flex h-16">
        <PayK12Logo className="aspect-auto" />
      </div>

      {/* Header */}
      <div className="relative mt-4 items-center justify-center text-center text-sm">
        <span className="relative h-[1px] bg-white px-2 font-sans text-black/60">Login to your Account</span>
        <div className="relative -top-[10px] -z-10 h-[1px] bg-black/20"></div>
      </div>

      {/* Email Field */}
      <div className="mt-4">
        <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">
          Email Address
        </label>
        <div className="relative mt-2">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Mail className="h-4 w-4 text-gray-400" />
          </div>
          <input
            id="username"
            name="username"
            type="email"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            onKeyUp={handleKeyPress}
            autoComplete="email"
            required
            disabled={isLoading}
            className={clsx(
              'block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
              validationErrors.username ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600',
            )}
            placeholder="Enter your email"
          />
          {validationErrors.username && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
          )}
        </div>
        {validationErrors.username && <p className="mt-1 text-sm text-red-600">{validationErrors.username}</p>}
      </div>

      {/* Password Field */}
      <div className="mt-4">
        <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
          Password
        </label>
        <div className="relative mt-2">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Lock className="h-4 w-4 text-gray-400" />
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onKeyUp={handleKeyPress}
            autoComplete="current-password"
            required
            disabled={isLoading}
            className={clsx(
              'block w-full rounded-md border-0 py-1.5 pl-10 pr-10 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
              validationErrors.password ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600',
            )}
            placeholder="Enter your password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        </div>
        {validationErrors.password && <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>}
      </div>

      {/* Forgot Password */}
      <div className="mt-4 flex items-center justify-end">
        <div className="text-sm leading-6">
          <a href="/pages/recover-password.html" className="font-semibold text-primary hover:text-primary-light">
            Forgot password?
          </a>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        <Button type="submit" disabled={isLoading} className="flex w-full items-center justify-center text-center">
          {isLoading ? (
            <>
              <RefreshCwIcon className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </div>
    </form>
  )
}

export default LoginForm
